export const Resources = {
  Dashboard: 'dashboard',
  SystemTransactions: 'systemtransactions',
  SystemWallets: 'systemwallets',
  SystemWhitelistedWallets: 'systemwhitelistedwallets',
  Merchants: 'merchants',
  MerchantTransactions: 'merchanttransactions',
  MerchantWallets: 'merchantwallets',
  MerchantWhitelistedWallets: 'merchantwhitelistedwallets',
  Accounts: 'accounts',
  Reports: 'reports',
  Logs: 'logs',
  Ledger: 'ledger',
  Tasks: 'tasks',
  Notifications: 'notifications',
  SystemStatus: 'systemstatus',
  BlockchainStatus: 'blockchainstatus',
  HangfireDashboard: 'hangfiredashboard',
  Staking: 'tronstaking',
  Sweeping: 'sweeping',
  SystemGL: 'systemgl',
  MerchantFeeProfiles: 'merchantfeeprofiles',
  MerchantAccounts: 'merchantaccounts',
  EthereumBlockchain: 'ethereumblockchain',
  TronBlockchain: 'tronblockchain',
  BscBlockchain: 'bscblockchain',
  Assets: 'assets',
  NotificationsConfiguration: 'notificationsconfiguration',
  Configuration: 'configuration',
  DelegationConfiguration: 'delegationconfiguration',
  RiskProfiles: 'riskprofiles',
  WalletLifecycle: 'walletlifecycle',
  AssetPricing: 'assetpricing',
} as const;

export type Resource = (typeof Resources)[keyof typeof Resources];

export const Scopes = {
  Access: 'access',
  Read: 'read',
  Update: 'update',
  Create: 'create',
  Approve: 'approve',
  Reject: 'reject',
  Revoke: 'revoke',
  Retry: 'retry',
  Speedup: 'speedup',
  Cancel: 'cancel',
  Process: 'process',
  CreateAdjustment: 'create-adjustment',
  ApproveAdjustment: 'approve-adjustment',
  RejectAdjustment: 'reject-adjustment',
  Enable: 'enable',
  Disable: 'disable',
  Sweep: 'sweep',
  EnableSweeping: 'enable-sweeping',
  DisableSweeping: 'disable-sweeping',
  ResetPassword: 'reset-password',
  Reset2FA: 'reset-2fa',
  Reset: 'reset',
  Export: 'export',
  History: 'history',
  Profile: 'profile',
  AccessDisabled: 'access-disabled',
  Acknowledge: 'acknowledge',
  SetRisk: 'set-risk',
  Stake: 'stake',
  Unstake: 'unstake',
  Undelegate: 'undelegate',
  ReleaseReservation: 'release-reservation',
  Vote: 'vote',
  ReadVotes: 'read-votes',
  CancelStake: 'cancelstake',
  ClaimAllRewards: 'claimallrewards',
  ReadClientCredentials: 'read-client-credentials',
  ResetClientCecret: 'reset-client-secret',
  MerchantsFilter: 'merchants-filter',
  ReadConfiguration: 'read-configuration',
  UpdateConfiguration: 'update-configuration',
  StartAutomation: 'start-automation',
  StopAutomation: 'stop-automation',
  ReadSystemConfiguration: 'read-system-configuration',
  CancelPrepared: 'cancel-prepared',
  Onboarding: 'onboarding',
  ProcessBlock: 'process-block',
  ProcessTransaction: 'process-transaction',
  ApproveDeposit: 'approve-deposit',
  RejectDeposit: 'reject-deposit',
  ReverseDeposit: 'reverse-deposit',
  SetExchangeRate: 'set-exchange-rate',
  RecordExchange: 'record-exchange',
  EditExchange: 'edit-exchange',
  DeleteExchange: 'delete-exchange',
  ReadUsers: 'read-users',
} as const;

export type Scope = (typeof Scopes)[keyof typeof Scopes];

export type RequiredPermission = {
  resource: Resource;
  scope?: Scope;
};

export const PermissionMatrix = {
  [Resources.Dashboard]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.ReadSystemConfiguration,
    Scopes.EnableSweeping,
    Scopes.DisableSweeping,
  ],
  [Resources.SystemTransactions]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Create,
    Scopes.Approve,
    Scopes.Reject,
    Scopes.Process,
    Scopes.Speedup,
    Scopes.Cancel,
    Scopes.Retry,
    Scopes.CancelPrepared,
    Scopes.ApproveDeposit,
    Scopes.RejectDeposit,
    Scopes.ReverseDeposit,
    Scopes.Acknowledge,
    Scopes.SetRisk,
    Scopes.SetExchangeRate,
    Scopes.RecordExchange,
    Scopes.EditExchange,
    Scopes.DeleteExchange,
  ],
  [Resources.SystemWallets]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Create,
    Scopes.Update,
    Scopes.Enable,
    Scopes.Disable,
    Scopes.Sweep,
    Scopes.EnableSweeping,
    Scopes.DisableSweeping,
  ],
  [Resources.SystemWhitelistedWallets]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Create,
    Scopes.Approve,
    Scopes.Reject,
    Scopes.Revoke,
  ],
  [Resources.Merchants]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Create,
    Scopes.Update,
    Scopes.Acknowledge,
    Scopes.Enable,
    Scopes.Disable,
    Scopes.EnableSweeping,
    Scopes.DisableSweeping,
    Scopes.AccessDisabled,
  ],
  [Resources.MerchantTransactions]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Create,
    Scopes.Approve,
    Scopes.Reject,
    Scopes.Process,
    Scopes.Speedup,
    Scopes.Cancel,
    Scopes.CreateAdjustment,
    Scopes.ApproveAdjustment,
    Scopes.RejectAdjustment,
    Scopes.Retry,
    Scopes.CancelPrepared,
    Scopes.ApproveDeposit,
    Scopes.RejectDeposit,
    Scopes.ReverseDeposit,
    Scopes.Acknowledge,
    Scopes.SetRisk,
    Scopes.SetExchangeRate,
  ],
  [Resources.MerchantWallets]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Create,
    Scopes.Update,
    Scopes.Enable,
    Scopes.Disable,
    Scopes.Sweep,
    Scopes.EnableSweeping,
    Scopes.DisableSweeping,
    Scopes.Onboarding,
  ],
  [Resources.MerchantWhitelistedWallets]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Create,
    Scopes.Approve,
    Scopes.Reject,
    Scopes.Revoke,
  ],
  [Resources.Accounts]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Profile,
    Scopes.Create,
    Scopes.Update,
    Scopes.Enable,
    Scopes.Disable,
    Scopes.ResetPassword,
    Scopes.Reset2FA,
    Scopes.Reset,
  ],
  [Resources.Reports]: [Scopes.Access, Scopes.Read, Scopes.Export, Scopes.MerchantsFilter],
  [Resources.Logs]: [Scopes.Access, Scopes.Read],
  [Resources.Ledger]: [Scopes.Access, Scopes.Read],
  [Resources.Tasks]: [Scopes.Access, Scopes.Read, Scopes.History, Scopes.Acknowledge],
  [Resources.Notifications]: [Scopes.Access, Scopes.Read],
  [Resources.SystemStatus]: [Scopes.Access, Scopes.Read],
  [Resources.BlockchainStatus]: [Scopes.Access, Scopes.Read],
  [Resources.HangfireDashboard]: [Scopes.Access],
  [Resources.Staking]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Stake,
    Scopes.Unstake,
    Scopes.Undelegate,
    Scopes.ReleaseReservation,
    Scopes.Vote,
    Scopes.CancelStake,
    Scopes.ClaimAllRewards,
    Scopes.ReadVotes,
  ],
  [Resources.SystemGL]: [Scopes.Access, Scopes.Read],
  [Resources.MerchantFeeProfiles]: [Scopes.Access, Scopes.Read],
  [Resources.MerchantAccounts]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.Create,
    Scopes.Update,
    Scopes.Enable,
    Scopes.Disable,
    Scopes.ResetPassword,
    Scopes.Reset2FA,
    Scopes.Reset,
  ],
  [Resources.EthereumBlockchain]: [Scopes.ProcessBlock, Scopes.ProcessTransaction],
  [Resources.TronBlockchain]: [Scopes.ProcessBlock, Scopes.ProcessTransaction],
  [Resources.BscBlockchain]: [Scopes.ProcessBlock, Scopes.ProcessTransaction],
  [Resources.Sweeping]: [
    Scopes.Access,
    Scopes.Read,
    Scopes.ReadConfiguration,
    Scopes.UpdateConfiguration,
    Scopes.StartAutomation,
    Scopes.StopAutomation,
    Scopes.MerchantsFilter,
  ],
  [Resources.Assets]: [Scopes.Access, Scopes.Read],
  [Resources.NotificationsConfiguration]: [Scopes.Access, Scopes.Read, Scopes.Update, Scopes.ReadUsers],
  [Resources.Configuration]: [Scopes.Access],
  [Resources.RiskProfiles]: [Scopes.Access, Scopes.Read, Scopes.Create, Scopes.Update],
  [Resources.DelegationConfiguration]: [Scopes.Access, Scopes.Read, Scopes.Update],
  [Resources.WalletLifecycle]: [Scopes.Access, Scopes.Read],
  [Resources.AssetPricing]: [Scopes.Read, Scopes.Create],
} as const;

export type AllowedScopeFor<R extends Resource> = (typeof PermissionMatrix)[R][number];

export function isScopeAllowedForResource<R extends Resource>(
  resource: R,
  scope: Scope | undefined,
): scope is AllowedScopeFor<R> {
  if (!scope) return false;
  const allowed: readonly Scope[] | undefined = PermissionMatrix[resource];
  return Array.isArray(allowed) ? allowed.includes(scope) : false;
}
